import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import ConfirmationModal from '../../../../../../common/components/xepp/confirmation-modal/ConfirmationModal';
import { NotificationFormProps } from './types';
import ToggleSlider from '../../../../../../common/components/xepp/toggle-slider/ToggleSlider';
import styles from './NotificationForm.module.scss';
import { Input } from '../../../../../customer/components';
import { useLazyGetBroadcastNotificationDetailQuery } from '../../../../../../service/notification-service';
import { BroadcastNotificationSubType } from '../../../../../../model/notifications/notifications';
import CircularProgressIndicator from '../../../../../../common/components/circular-progress-indicator/CircularProgressIndicator';

interface BoxAnnouncementFormValues {
    turnOn: boolean;
    title: string;
    message: string;
}

const BoxAnnouncementForm = ({ onSubmit, onEdit, onExistingChange }: NotificationFormProps) => {
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [pendingData, setPendingData] = useState<BoxAnnouncementFormValues | null>(null);

    const [fetchDetail, { data: existingData, isLoading }] = useLazyGetBroadcastNotificationDetailQuery();

    const { control, handleSubmit, reset } = useForm<BoxAnnouncementFormValues>({
        defaultValues: {
            turnOn: true,
            title: '',
            message: '',
        },
    });

    useEffect(() => {
        fetchDetail({ type: BroadcastNotificationSubType.BoxNotification });
    }, []);

    useEffect(() => {
        if (!existingData) return;
        onExistingChange?.(!!existingData.id);
        reset({
            turnOn: existingData.turnOnOff,
            title: existingData.title ?? '',
            message: existingData.message ?? '',
        });
    }, [existingData]);

    const onFormSubmit = (data: BoxAnnouncementFormValues) => {
        setPendingData(data);
        setIsConfirmOpen(true);
    };

    const handleConfirm = () => {
        if (!pendingData) return;
        setIsConfirmOpen(false);
        const payload = { notificationType: 'box' as const, ...pendingData };
        if (existingData?.id && onEdit) {
            onEdit(existingData.id, payload);
        } else {
            onSubmit(payload);
        }
        setPendingData(null);
    };

    if (isLoading) {
        return <div className={styles.loaderWrapper}><CircularProgressIndicator width={24} height={24} /></div>;
    }

    return (
        <>
        <form id="notification-form" className={styles.form} onSubmit={handleSubmit(onFormSubmit)}>
            <Controller
                name="turnOn"
                control={control}
                render={({ field }) => (
                    <div className={styles.toggleRow}>
                        <div className={styles.toggleRowMain}>
                            <span className={styles.toggleLabel}>Turn On/Off</span>
                            <ToggleSlider isActive={field.value} onChange={field.onChange} />
                        </div>
                    </div>
                )}
            />
            <Controller
                name="title"
                control={control}
                rules={{ required: 'Title is required.', maxLength: { value: 100, message: 'Title must not exceed 100 characters.' } }}
                render={({ field, fieldState }) => (
                    <div className={styles.field}>
                        <div className={styles.labelRow}>
                            <label className={styles.label}>Title</label>
                            <span className={styles.charCount}>{field.value.length} / 100</span>
                        </div>
                        <Input
                            variant="light"
                            value={field.value}
                            onChange={field.onChange}
                            placeholder="Enter title"
                            error={fieldState.error?.message}
                        />
                    </div>
                )}
            />
            <Controller
                name="message"
                control={control}
                rules={{ required: 'Message is required.', maxLength: { value: 200, message: 'Message must not exceed 200 characters.' } }}
                render={({ field, fieldState }) => (
                    <div className={styles.field}>
                        <div className={styles.labelRow}>
                            <label className={styles.label}>Message</label>
                            <span className={styles.charCount}>{field.value.length} / 200</span>
                        </div>
                        <textarea
                            className={`${styles.textarea} ${fieldState.error ? styles.textareaError : ''}`}
                            value={field.value}
                            onChange={field.onChange}
                            rows={4}
                        />
                        {fieldState.error && <span className={styles.errorText}>{fieldState.error.message}</span>}
                    </div>
                )}
            />
        </form>
        <ConfirmationModal
            isOpen={isConfirmOpen}
            onClose={() => setIsConfirmOpen(false)}
            onConfirm={handleConfirm}
            title="Send Box Announcement"
            message="Send box announcement to all customers?"
            confirmText="Confirm"
            cancelText="Cancel"
            variant="info"
        />
        </>
    );
};

export default BoxAnnouncementForm;
